import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Eye, Users, ShoppingBag, Banknote, Mail, LogOut, RefreshCw, Snowflake, TrendingUp, Globe,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQ = { withCredentials: true };

// Status badge color lookup — replaces a nested ternary for readability.
const STATUS_STYLE = {
  paid: "bg-[#9BE800]",
  initiated: "bg-[#FFCC00]",
  unpaid: "bg-[#FF3D7F] text-white",
  expired: "bg-[#FF3D7F] text-white",
};
const statusClass = (s) => STATUS_STYLE[s] || "bg-[#FF3D7F] text-white";

const Stat = ({ Icon, label, value, sub, bg = "#FAF9F6" }) => (
  <div className="border-[3px] border-[#0C0A09] shadow-[5px_5px_0_0_#0C0A09] p-5" style={{ background: bg }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">{label}</span>
      <div className="w-9 h-9 rounded-full border-[3px] border-[#0C0A09] bg-white flex items-center justify-center">
        <Icon size={16} strokeWidth={2.75} />
      </div>
    </div>
    <div className="font-display text-5xl leading-none mt-3">{value}</div>
    {sub && <div className="text-xs font-bold mt-2 opacity-70">{sub}</div>}
  </div>
);

const TinyBars = ({ data }) => {
  if (!data?.length) return <div className="text-sm opacity-60 py-8 text-center">Sin datos todavía.</div>;
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1.5 h-40">
      {data.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
          <div className="text-[10px] font-bold opacity-70">{d.count}</div>
          <div
            className="w-full bg-[#FF3D7F] border-[2px] border-[#0C0A09]"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: "4px" }}
            title={`${d.day}: ${d.count} visitas, ${d.unique} únicos`}
          />
          <div className="text-[9px] font-bold opacity-60 rotate-[-45deg] origin-left whitespace-nowrap">
            {d.day.slice(5)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState("visits");
  const [error, setError] = useState("");

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, REQ);
    } catch (err) {
      // Logout failure should not block the redirect — just log it.
      console.warn("Logout request failed (non-fatal):", err?.message);
    }
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, v, o] = await Promise.all([
        axios.get(`${API}/admin/stats`, REQ),
        axios.get(`${API}/admin/visits?limit=50`, REQ),
        axios.get(`${API}/admin/orders?limit=50`, REQ),
      ]);
      setStats(s.data);
      setVisits(v.data.visits || []);
      setOrders(o.data.orders || []);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        return;
      }
      setError("No se pudieron cargar los datos.");
    }
    setLoading(false);
  }, [logout]);

  // Verify session via httpOnly cookie. Redirect to /admin/login if invalid.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, REQ);
        if (cancelled) return;
        setAuthChecked(true);
        load();
      } catch {
        if (!cancelled) navigate("/admin/login", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [load, navigate]);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="font-bold uppercase tracking-widest text-sm">Verificando sesión...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-[#0C0A09] text-white border-b-[3px] border-[#0C0A09]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#9BE800] border-[3px] border-white flex items-center justify-center">
              <Snowflake size={18} strokeWidth={3} className="text-[#0C0A09]" />
            </div>
            <span className="font-display text-2xl tracking-tight">VELORA · ADMIN</span>
          </div>
          <div className="flex gap-2">
            <button data-testid="admin-test-email-btn" onClick={async () => {
              try {
                const res = await axios.post(`${API}/admin/test-email`, {}, REQ);
                alert(`✅ Email de prueba enviado a ${res.data.sent_to}. Revisa tu Gmail (también la carpeta de spam).`);
              } catch (err) {
                const msg = err?.response?.data?.detail || err?.message;
                alert(`❌ ${msg}`);
              }
            }} className="px-4 py-2 rounded-full border-[2px] border-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#9BE800] hover:text-[#0C0A09] transition-colors">
              <Mail size={14} strokeWidth={3} /> Probar email
            </button>
            <button data-testid="admin-refresh-btn" onClick={load} className="px-4 py-2 rounded-full border-[2px] border-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 hover:bg-white hover:text-[#0C0A09] transition-colors">
              <RefreshCw size={14} strokeWidth={3} /> Refrescar
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="px-4 py-2 rounded-full border-[2px] border-white text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#FF3D7F] transition-colors">
              <LogOut size={14} strokeWidth={3} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-10 py-10">
        {loading && !stats && <div className="text-center py-20 font-bold uppercase tracking-widest text-sm">Cargando...</div>}
        {error && <div data-testid="admin-error" className="mb-4 text-sm font-bold text-[#FF3D7F]">{error}</div>}

        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-8">
              <Stat Icon={Eye} label="Visitas hoy" value={stats.visits.today} sub={`${stats.unique.today} únicos`} bg="#FFCC00" />
              <Stat Icon={TrendingUp} label="Visitas 7 días" value={stats.visits.week} sub={`${stats.unique.week} únicos`} bg="#9BE800" />
              <Stat Icon={Users} label="Visitantes totales" value={stats.unique.total} sub={`${stats.visits.total} visitas`} bg="#1FB6FF" />
              <Stat Icon={Banknote} label="Ingresos" value={`€${stats.orders.revenue_usd}`} sub={`${stats.orders.paid} pagados`} bg="#FF3D7F" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-10">
              <Stat Icon={ShoppingBag} label="Pedidos totales" value={stats.orders.total} sub={`${stats.orders.pending} pendientes`} />
              <Stat Icon={Mail} label="Newsletter" value={stats.newsletter.total} sub="suscritos" />
              <Stat Icon={Globe} label="Idiomas" value={stats.languages.length} sub={stats.languages.map(l => `${l.language || "?"}: ${l.count}`).join(" · ").slice(0, 40)} />
              <Stat Icon={Eye} label="Visitas mes" value={stats.visits.month} sub="últimos 30 días" />
            </div>

            <div className="border-[3px] border-[#0C0A09] shadow-[6px_6px_0_0_#0C0A09] p-6 md:p-8 bg-white mb-10">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-3xl leading-none">Visitas — últimos 14 días</h2>
                <span className="nb-badge text-[10px]">por día</span>
              </div>
              <TinyBars data={stats.chart_14d} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
              <div className="border-[3px] border-[#0C0A09] shadow-[5px_5px_0_0_#0C0A09] bg-white p-6">
                <h3 className="font-display text-2xl mb-4">Páginas más vistas</h3>
                <div className="space-y-2">
                  {stats.top_pages.length === 0 && <div className="text-sm opacity-60">Sin datos.</div>}
                  {stats.top_pages.map((p) => (
                    <div key={p.path} className="flex items-center justify-between text-sm border-b-2 border-dashed border-[#0C0A09]/20 pb-2">
                      <code className="font-mono">{p.path || "/"}</code>
                      <span className="font-bold">{p.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-[3px] border-[#0C0A09] shadow-[5px_5px_0_0_#0C0A09] bg-white p-6">
                <h3 className="font-display text-2xl mb-4">Idiomas</h3>
                <div className="space-y-2">
                  {stats.languages.length === 0 && <div className="text-sm opacity-60">Sin datos.</div>}
                  {stats.languages.map((l) => (
                    <div key={l.language || "unknown"} className="flex items-center justify-between text-sm border-b-2 border-dashed border-[#0C0A09]/20 pb-2">
                      <span className="font-bold uppercase tracking-widest">{l.language || "—"}</span>
                      <span className="font-bold">{l.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button data-testid="admin-tab-visits" onClick={() => setTab("visits")} className={`px-4 py-2 border-[3px] border-[#0C0A09] font-bold uppercase tracking-widest text-xs rounded-full ${tab === "visits" ? "bg-[#0C0A09] text-white" : "bg-white"}`}>Visitas recientes</button>
              <button data-testid="admin-tab-orders" onClick={() => setTab("orders")} className={`px-4 py-2 border-[3px] border-[#0C0A09] font-bold uppercase tracking-widest text-xs rounded-full ${tab === "orders" ? "bg-[#0C0A09] text-white" : "bg-white"}`}>Pedidos</button>
            </div>

            <div className="border-[3px] border-[#0C0A09] shadow-[5px_5px_0_0_#0C0A09] bg-white overflow-hidden">
              {tab === "visits" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="visits-table">
                    <thead className="bg-[#0C0A09] text-white">
                      <tr>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Fecha</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Página</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Idioma</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Origen</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Visitante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits.length === 0 && (
                        <tr><td colSpan={5} className="p-8 text-center opacity-60">Aún no hay visitas registradas.</td></tr>
                      )}
                      {visits.map((v) => (
                        <tr key={v.id} className="border-b border-[#0C0A09]/10 hover:bg-[#FAF9F6]">
                          <td className="p-3 font-mono text-xs">{new Date(v.created_at).toLocaleString("es-ES")}</td>
                          <td className="p-3 font-mono text-xs">{v.path}</td>
                          <td className="p-3 font-bold uppercase">{v.language}</td>
                          <td className="p-3 font-mono text-xs opacity-70 max-w-[200px] truncate">{v.referrer || "directo"}</td>
                          <td className="p-3 font-mono text-xs opacity-60">{v.visitor_id?.slice(0, 8)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="orders-table">
                    <thead className="bg-[#0C0A09] text-white">
                      <tr>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Fecha</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Cliente</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Items</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Total</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Estado</th>
                        <th className="text-left p-3 text-[10px] uppercase tracking-widest">Envío</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 && (
                        <tr><td colSpan={6} className="p-8 text-center opacity-60">Aún no hay pedidos.</td></tr>
                      )}
                      {orders.map((o) => {
                        const ship = o.shipping || {};
                        const status = o.payment_status || "—";
                        const itemsCount = (o.items || []).reduce((s, i) => s + (i.qty || 0), 0);
                        return (
                          <tr key={o.id} className="border-b border-[#0C0A09]/10 hover:bg-[#FAF9F6]">
                            <td className="p-3 font-mono text-xs">{new Date(o.created_at).toLocaleString("es-ES")}</td>
                            <td className="p-3 text-xs">
                              <div className="font-bold">{ship.full_name || "—"}</div>
                              <div className="opacity-70">{ship.email || o.customer_email || "—"}</div>
                            </td>
                            <td className="p-3 text-xs">{itemsCount} vaso{itemsCount === 1 ? "" : "s"}</td>
                            <td className="p-3 font-bold">€{(o.amount || 0).toFixed(2)}</td>
                            <td className="p-3"><span className={`px-2 py-1 border-2 border-[#0C0A09] rounded-full text-[10px] font-bold uppercase tracking-widest ${statusClass(status)}`}>{status}</span></td>
                            <td className="p-3 text-xs opacity-80 max-w-[260px]">
                              {ship.address_line1 ? `${ship.address_line1}, ${ship.city || ""} ${ship.postal_code || ""} ${ship.country || ""}` : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
